import { useState, useMemo, useEffect } from "react";
import type { Contact } from "../types/contact";
import type { FilterState } from "../components/contacts/ContactsFilter";

export const useContactsFilter = (
  contacts: Contact[],
  externalFilterState?: FilterState
) => {
  const [filterState, setFilterState] = useState<FilterState>({
    dateFilter: "all",
    sourceKey: undefined,
    startDate: "",
    endDate: "",
    selectedMonth: undefined,
    searchQuery: "",
  });

  // Update internal filter state when external state changes
  useEffect(() => {
    if (externalFilterState) {
      setFilterState(externalFilterState);
    }
  }, [externalFilterState]);

  const filteredContacts = useMemo(() => {
    // If external filter state is provided, the filtering is done on the server side
    // so we just return all contacts as they are already filtered
    if (externalFilterState) {
      return contacts;
    }

    // Otherwise, apply local filtering (for backward compatibility)
    return contacts.filter((contact) => {
      const contactDate = new Date(
        contact.joinDate.split("/").reverse().join("-")
      );
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Search filtering is now handled on the server side

      // Apply source filter if specified
      if (filterState.sourceKey && contact.source !== filterState.sourceKey) {
        return false;
      }

      // Apply date filter
      switch (filterState.dateFilter) {
        case "all":
          return true;

        case "today": {
          const todayStart = new Date(today);
          const todayEnd = new Date(today);
          todayEnd.setHours(23, 59, 59, 999);
          return contactDate >= todayStart && contactDate <= todayEnd;
        }

        case "this_week": {
          const weekStart = new Date(today);
          const dayOfWeek = today.getDay();
          const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
          weekStart.setDate(diff);
          weekStart.setHours(0, 0, 0, 0);
          return contactDate >= weekStart;
        }

        case "month": {
          if (!filterState.selectedMonth) return true;
          const monthStart = new Date(
            today.getFullYear(),
            filterState.selectedMonth - 1,
            1
          );
          const monthEnd = new Date(
            today.getFullYear(),
            filterState.selectedMonth,
            0
          );
          monthEnd.setHours(23, 59, 59, 999);
          return contactDate >= monthStart && contactDate <= monthEnd;
        }

        case "dateRange": {
          if (!filterState.startDate || !filterState.endDate) return true;
          const start = new Date(filterState.startDate);
          const end = new Date(filterState.endDate);
          end.setHours(23, 59, 59, 999);
          return contactDate >= start && contactDate <= end;
        }

        default:
          return true;
      }
    });
  }, [contacts, filterState, externalFilterState]);

  const applyFilter = (newFilterState: FilterState) => {
    setFilterState(newFilterState);
  };

  const handleMonthChange = (month: number) => {
    setFilterState((prev) => ({
      ...prev,
      selectedMonth: month,
      dateFilter: "month",
    }));
  };

  return {
    filteredContacts,
    filterState,
    applyFilter,
    handleMonthChange,
  };
};
